/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  User, 
  Settings, 
  LogOut,
  ChevronDown,
  BarChart3,
  TrendingUp,
  Phone,
  Mail,
  MessageCircle,
  Headphones,
  LayoutGrid,
  ListOrdered,
  FileText
} from 'lucide-react';
import { IoLogoWhatsapp } from "react-icons/io";
import { useAuth } from '@/hooks/useAuth';
import { Button } from '../ui/button';
import { cn } from '../../utils/helpers';

const navigationItems = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: BarChart3
  },
  {
    name: 'Strategies',
    href: '/strategies',
    icon: LayoutGrid
  },
  {
    name: 'Orders/Trades',
    href: '/orders-trades',
    icon: ListOrdered
  },
  {
    name: 'Wealth Report',
    href: '/report',
    icon: FileText
  }
];

export const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isCallBackMenuOpen, setIsCallBackMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleRequestCallBack = () => {
    // Open cal.com booking page in new tab
    window.open('https://cal.com/iamclearmind', '_blank');
    setIsCallBackMenuOpen(false);
  };

  // Show dashboard navigation
  const showDashboardNav = true;

  // Don't show navbar on auth pages for non-authenticated users
  if (!isAuthenticated && ['/login', '/verify-email'].includes(location.pathname)) {
    return null;
  }

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="sticky top-0 z-50 w-full bg-black border-b border-gray-800"
    >
      <div className="container mx-auto px-6">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center space-x-2"
          >
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-black" />
              </div>
              <span className="text-xl font-bold text-white">
                Strategy
              </span>
            </Link>
          </motion.div>

          {/* Desktop Navigation - Centered - Only show on dashboard page */}
          {isAuthenticated && showDashboardNav && (
            <div className="hidden md:flex items-center justify-center flex-1">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                
                return (
                  <motion.div
                    key={item.href}
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link
                      to={item.href}
                      className={cn(
                        'flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-white/10 text-white'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.name}</span>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Right side */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                {/* Support/Call Back Menu */}
                <div 
                  className="relative"
                  onMouseEnter={() => setIsCallBackMenuOpen(true)}
                  onMouseLeave={() => setIsCallBackMenuOpen(false)}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center space-x-2 text-white/70 hover:text-white hover:bg-white/5 px-3"
                  >
                    <Headphones className="w-4 h-4" />
                    <span className="hidden md:block text-sm font-medium">Support</span>
                  </Button>

                  <AnimatePresence>
                    {isCallBackMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-6"
                      >
                        {/* Header */}
                        <div className="mb-4">
                          <div className="flex items-center mb-2">
                            <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center mr-3">
                              <span className="text-white font-semibold text-sm">C</span>
                            </div>
                            <div>
                              <h3 className="text-black font-semibold text-sm">Hi, Your Account Manager</h3>
                              <p className="text-gray-600 text-xs"></p>
                            </div>
                          </div>
                      
                        </div>

                        {/* Request Call Back Button */}
                        <Button 
                          onClick={handleRequestCallBack}
                          className="w-full bg-black text-white mb-4 py-2"
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          Request Call Back
                        </Button>

                        {/* Contact Information */}
                        <div className="space-y-2">
                          {/* Email Row */}
                          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                            <a 
                              href="mailto:admin@iamclearmind.com"
                              className="flex items-center hover:text-blue-600 transition-colors"
                            >
                              <Mail className="w-4 h-4 text-gray-600 mr-3" />
                              <span className="text-sm text-gray-700">admin@iamclearmind.com</span>
                            </a>
                          </div>
                          
                          {/* WhatsApp Row */}
                          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                              <IoLogoWhatsapp className="w-4 h-4 text-green-600 mr-2" />
                              <span className="text-sm text-gray-700"><a href="https://wa.me/918766097532?text=Hi, I need help with my account" target="_blank" rel="noopener noreferrer">+91 8766097532</a></span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Profile Menu */}
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="flex items-center space-x-1 sm:space-x-2 text-white/70 hover:text-white hover:bg-white/5 px-0 sm:px-3"
                  >
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-full flex items-center justify-center">
                      <User className="w-3 h-3 sm:w-4 sm:h-4 text-black" />
                    </div>
                    <span className="hidden md:block text-sm font-medium">
                      {user?.name?.split('@')[0] || 'User'}
                    </span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>

                  <AnimatePresence>
                    {isProfileMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
                      >
                        <div className="px-2 py-2 border-b border-gray-200">
                          <p className="text-sm font-medium text-black">
                            {user?.name || 'Guest User'}
                          </p>
                          <p className="text-xs text-gray-600">
                            {user?.email || 'guest@example.com'}
                          </p>
                        </div>

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Sign out</span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile Menu Button - Only show on dashboard page */}
                {showDashboardNav && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="md:hidden text-white/70 hover:text-white hover:bg-white/5"
                  >
                    {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                  </Button>
                )}
              </>
            ) : (
              /* Auth buttons for non-authenticated users */
              <div className="flex items-center space-x-3">
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-white/70 hover:text-white hover:bg-white/5"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-white text-black hover:bg-white/90">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && isAuthenticated && showDashboardNav && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-gray-800 py-4"
            >
              <div className="flex flex-col space-y-2">
                {navigationItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn(
                        'flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-white/10 text-white'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}

                {/* Mobile Profile Actions */}
                <div className="pt-2 border-t border-gray-800 mt-2">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors rounded-lg"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};
